import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileAudio, Loader2, PhoneOutgoing, PhoneIncoming } from 'lucide-react';
import { api } from '@/api/client';
import { CallRecordingPlayer } from './CallRecordingPlayer';
import { Modal } from '@/components/ui/Modal';

/**
 * CALLS & RECORDING for one shipment.
 *
 * The IVR module already lists every call platform-wide, but reaching a specific order's calls
 * meant leaving Orders, opening IVR and searching the AWB. This puts the same rows — and the same
 * recording download — on the order itself, which is where follow-up actually happens.
 *
 * The badge shows the call count so the button is not a blind click: an order with no calls says
 * so on the row rather than opening an empty modal.
 */

type Call = {
  id: string;
  direction: 'OUTBOUND' | 'INBOUND';
  status: string;
  reason?: string | null;
  fromNumber: string;
  toNumber: string;
  durationSec?: number | null;
  startedAt?: string | null;
  startedAtIsExact?: boolean;
  completedAt?: string | null;
  recordingUrl?: string | null;
  providerCallId?: string | null;
  actorUserName?: string | null;
};
type Res = { total: number; withRecording: number; items: Call[] };

const dt = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
/** A call the provider never reported a duration for shows a dash, never a misleading 0:00. */
const dur = (s?: number | null) => (s == null ? '—' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);

const STATUS_STYLE: Record<string, string> = {
  COMPLETED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  CONNECTED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  INITIATED: 'bg-amber-100 text-amber-800 border-amber-200',
  NO_ANSWER: 'bg-slate-100 text-slate-600 border-slate-200',
  BUSY: 'bg-slate-100 text-slate-600 border-slate-200',
  FAILED: 'bg-rose-100 text-rose-700 border-rose-200',
};

interface Props {
  orderId?: string | number;
  awbNo?: string | null;
  lrNo?: string | null;
  /** Order screens render this in a tight action row; the detail page has room for a word. */
  label?: string;
  /**
   * Fetch the count before the button is pressed, so it can show "3". Only for single-order
   * screens — turning this on in a list would fire one request per row, 50 per page, to decide
   * a badge nobody has asked to see yet.
   */
  prefetch?: boolean;
  className?: string;
}

export function CallRecordingsButton({ orderId, awbNo, lrNo, label, prefetch = false, className = '' }: Props) {
  const [open, setOpen] = useState(false);
  const params = {
    ...(orderId ? { orderId: String(orderId) } : {}),
    ...(awbNo ? { awbNo } : {}),
    ...(lrNo ? { lrNo } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['order-calls', params],
    queryFn: async () => (await api.get<Res>('/ivr/order-calls', { params })).data,
    enabled: Object.keys(params).length > 0 && (prefetch || open),
    staleTime: 60_000,
  });

  // `undefined` until the first fetch — kept distinct from 0 so an unfetched button does not
  // claim "no calls" before it has looked.
  const total = data?.total;
  const withRec = data?.withRecording ?? 0;

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        title={
          total === undefined
            ? 'Calls and recordings for this shipment'
            : total === 0
              ? 'No calls have been placed against this shipment yet'
              : `${total} call(s) · ${withRec} recording(s)`
        }
        className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs transition ${
          withRec > 0
            ? 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100'
            : total === 0
              ? 'bg-white text-slate-400 border-dashed border-slate-200 hover:bg-slate-50'
              : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
        } ${className}`}
      >
        {isLoading ? <Loader2 size={12} className="animate-spin" /> : <FileAudio size={12} />}
        {label}
        {!!total && <span className="font-semibold tabular-nums">{total}</span>}
      </button>

      {open && (
        <Modal
          isOpen
          onClose={() => setOpen(false)}
          title="Calls & Recording"
          subtitle={awbNo || lrNo ? `Shipment ${awbNo || lrNo}` : undefined}
          maxWidth="4xl"
        >
          {isLoading && <div className="py-10 text-center"><Loader2 size={20} className="mx-auto animate-spin text-primary" /></div>}

          {!isLoading && !data && (
            <div className="py-10 text-center text-sm text-rose-600">Could not load the calls for this shipment.</div>
          )}

          {!isLoading && data && data.total === 0 && (
            <div className="py-10 text-center text-sm text-slate-500">
              No call has been placed against this shipment yet. Use <b>Click to Call</b> on the order to place one.
            </div>
          )}

          {!isLoading && data && data.total > 0 && (
            <div className="space-y-3">
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">When</th>
                      <th className="text-left px-3 py-2">Called by</th>
                      <th className="text-left px-3 py-2">From</th>
                      <th className="text-left px-3 py-2">To</th>
                      <th className="text-left px-3 py-2">Reason</th>
                      <th className="text-right px-3 py-2">Duration</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data!.items.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                          {dt(c.startedAt)}
                          {/* A start time inferred from when WE dialled is not the vendor's own. */}
                          {!c.startedAtIsExact && (
                            <span className="text-slate-400" title="Provider never reported a start time — showing when the call was placed"> *</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-700">
                          <span className="inline-flex items-center gap-1">
                            {c.direction === 'INBOUND'
                              ? <PhoneIncoming size={11} className="text-slate-400" />
                              : <PhoneOutgoing size={11} className="text-slate-400" />}
                            {c.actorUserName ?? (c.direction === 'INBOUND' ? 'Inbound' : '—')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-slate-600">{c.fromNumber || '—'}</td>
                        <td className="px-3 py-2 text-xs font-mono text-slate-900">{c.toNumber}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{c.reason ?? '—'}</td>
                        <td className="px-3 py-2 text-xs text-right tabular-nums">{dur(c.durationSec)}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className={`px-2 py-0.5 rounded border ${STATUS_STYLE[c.status] ?? 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {c.recordingUrl
                            ? <CallRecordingPlayer callId={c.id} compact />
                            : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* A row of dashes with no explanation reads as "the feature is broken". It isn't:
                  we store a recording the moment the provider pushes one, and none has arrived. */}
              {withRec === 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <b>No recordings on these calls.</b> A recording is stored the moment the IVR provider pushes
                  it to our webhook — none has been received for this shipment. The webhook URL and the exact
                  field names to hand the provider are in <b>Settings → IVR</b>.
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
